import { useEffect, useState } from 'react';
import { useActiveProject, useService } from '../../../../../hooks';
import { AxiosResponse } from 'axios';
import {
  AttachmentSearchData,
  buildAttachmentSearchQuery,
} from './buildAttachmentSearchQuery';
import {
  generateQuestionnaireSearchQuery,
  QuestionnaireSearchData,
} from './buildQuestionnaireSearchQuery';
import { Attachment, PRIMARY_SURVEY_ID_SYSTEM } from './Attachment';
import {
  getSummaryArtifactSourceReference,
  isDocumentExtractionV2SummaryArtifact,
} from './documentExtractionV2Summary';
import {
  useReportErrorToBugsnag,
  useReportInfoToBugsnag,
} from '../../../../../util/bugsnagReporting';
import FhirSearchService from '../../../../../services/FhirSearchService';

const fss = new FhirSearchService();

type QuestionnaireHit = QuestionnaireSearchData['hits']['hits'][number];

const getPrimarySurveyId = (
  meta: { tag?: { system?: string; code?: string }[] } | undefined,
): string | undefined =>
  meta?.tag?.find(tag => tag.system === PRIMARY_SURVEY_ID_SYSTEM)?.code;

export const useAttachmentArbiterState = (attachments?: string[]) => {
  const [project] = useActiveProject();
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [hasEverLoaded, setHasEverLoaded] = useState(false);
  const [attachmentMap, setAttachmentMap] = useState<
    Record<string, Attachment>
  >({});
  const [questionnairesByPrimaryId, setQuestionnairesByPrimaryId] = useState<
    Record<string, QuestionnaireHit>
  >({});
  const [fssSearch] = useService(fss.search);

  const reportErrorToBugsnag = useReportErrorToBugsnag();
  const reportInfoToBugsnag = useReportInfoToBugsnag();
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let cancelled = false;

    const fetchAttachments = async () => {
      if (attachments === undefined) {
        return;
      }

      const toFetch = attachments.filter(ref => !attachmentMap[ref]);
      if (toFetch.length === 0) {
        setAttachmentsLoading(false);
        if (!hasEverLoaded) {
          setHasEverLoaded(true);
        }
        return;
      }

      setAttachmentsLoading(true);
      try {
        const attachmentSearchResponse: AxiosResponse<AttachmentSearchData> =
          await fssSearch(
            project.id,
            buildAttachmentSearchQuery(toFetch),
            undefined,
            { queryId: 'ai-tools-attachment-search' },
          );

        if (cancelled) return;

        // Collect any primary-survey-ids referenced by the new hits that we have
        // not already fetched questionnaire metadata for.
        const newPrimaryIds = Array.from(
          new Set(
            attachmentSearchResponse.data.hits.hits
              .map(hit => getPrimarySurveyId(hit._source.meta))
              .filter(
                (id): id is string => !!id && !questionnairesByPrimaryId[id],
              ),
          ),
        );

        const newQuestionnaireHits: QuestionnaireHit[] = newPrimaryIds.length
          ? (
              (await fssSearch(
                project.id,
                generateQuestionnaireSearchQuery(newPrimaryIds),
                undefined,
                { queryId: 'ai-tools-questionnaire-search' },
              )) as AxiosResponse<QuestionnaireSearchData>
            ).data.hits.hits
          : [];

        if (cancelled) return;

        const mergedQuestionnaires: Record<string, QuestionnaireHit> = {
          ...questionnairesByPrimaryId,
        };
        newQuestionnaireHits.forEach(hit => {
          const id = getPrimarySurveyId(hit._source.meta);
          if (id) {
            mergedQuestionnaires[id] = hit;
          }
        });

        const ctx = {
          questionnaireData: {
            hits: { hits: Object.values(mergedQuestionnaires) },
          },
        };

        const hits = attachmentSearchResponse.data.hits.hits;

        // Index the just-fetched resources so a summary artifact's source document
        // can be resolved without an extra round trip when it was also cited.
        const resourceByReference: Record<string, fhir.Resource> = {};
        hits.forEach(hit => {
          const resource = hit._source as fhir.Resource;
          if (resource.resourceType && resource.id) {
            resourceByReference[`${resource.resourceType}/${resource.id}`] =
              resource;
          }
        });

        // Document Extraction V2 AI Summary artifacts carry only inline markdown
        // and render empty when cited directly. Map each one's citation to the
        // source document it was derived from so the AR shows a viewable report.
        const summarySourceByArtifactId: Record<string, string> = {};
        hits.forEach(hit => {
          const resource = hit._source;
          if (isDocumentExtractionV2SummaryArtifact(resource) && resource.id) {
            const sourceReference = getSummaryArtifactSourceReference(resource);
            if (sourceReference) {
              summarySourceByArtifactId[resource.id] = sourceReference;
            }
          }
        });

        const sourceReferencesToFetch = Array.from(
          new Set(Object.values(summarySourceByArtifactId)),
        ).filter(ref => !resourceByReference[ref] && !attachmentMap[ref]);

        if (sourceReferencesToFetch.length) {
          const sourceSearchResponse: AxiosResponse<AttachmentSearchData> =
            await fssSearch(
              project.id,
              buildAttachmentSearchQuery(sourceReferencesToFetch),
              undefined,
              { queryId: 'ai-tools-attachment-source-search' },
            );

          if (cancelled) return;

          sourceSearchResponse.data.hits.hits.forEach(hit => {
            const resource = hit._source as fhir.Resource;
            if (resource.resourceType && resource.id) {
              resourceByReference[`${resource.resourceType}/${resource.id}`] =
                resource;
            }
          });
        }

        const resolveSummarySourceAttachment = (
          artifactId: string | undefined,
        ): Attachment | undefined => {
          const sourceReference = artifactId
            ? summarySourceByArtifactId[artifactId]
            : undefined;
          if (!sourceReference) return undefined;
          const sourceResource =
            resourceByReference[sourceReference] ??
            attachmentMap[sourceReference]?.resource;
          return sourceResource
            ? Attachment.from(ctx, sourceResource)
            : undefined;
        };

        const newEntries: Record<string, Attachment> = {};
        hits.forEach(hit => {
          const attachment = Attachment.from(ctx, hit._source);
          if (isDocumentExtractionV2SummaryArtifact(hit._source)) {
            const sourceAttachment = resolveSummarySourceAttachment(
              hit._source.id,
            );
            if (sourceAttachment) {
              // Keep the artifact reference as the map key (the impression cites
              // the artifact) but resolve it to the source document's attachment,
              // so both the inline chip and the modal open the viewable report.
              newEntries[attachment.identifier] = sourceAttachment;
              return;
            }
          }
          newEntries[attachment.identifier] = attachment;
        });

        if (cancelled) return;

        setQuestionnairesByPrimaryId(mergedQuestionnaires);
        // Merge with existing entries last-wins for previous values so that
        // `addAttachment`-injected `TemporaryAttachment`s survive a delta fetch
        // even if the same reference is later returned by FSS.
        setAttachmentMap(prev => ({ ...newEntries, ...prev }));
        setHasEverLoaded(true);

        const missing = toFetch.filter(ref => !newEntries[ref]);
        if (missing.length) {
          reportInfoToBugsnag(
            `Was not able to load all attachments: ${missing.join(', ')}`,
          );
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (!cancelled) {
          setAttachmentsLoading(false);
        }
      }
    };

    fetchAttachments();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, fssSearch, project.id]);

  useEffect(() => {
    if (error) {
      reportErrorToBugsnag(error);
    }
  }, [error, reportErrorToBugsnag]);

  return {
    loading: attachmentsLoading || !hasEverLoaded,
    attachmentMap,
    setAttachmentMap,
  };
};
